import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

// Create axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      // Redirect to login, preserving the base path
      const currentPath = window.location.pathname;
      let loginPath = '/login';
      if (currentPath.startsWith('/admin')) {
        loginPath = '/admin/login';
      } else if (currentPath.startsWith('/tech')) {
        loginPath = '/tech/login';
      }
      window.location.href = loginPath;
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => apiClient.post('/auth/login', credentials),
  register: (userData) => apiClient.post('/auth/register', userData),
  getProfile: () => apiClient.get('/auth/profile'),
  updateProfile: (data) => apiClient.put('/auth/profile', data),
  changePassword: (data) => apiClient.put('/auth/change-password', data),
  forgotPassword: (email) => apiClient.post('/auth/forgot-password', { email }),
  resetPassword: (data) => apiClient.post('/auth/reset-password', data),
  verifyResetToken: (token) => apiClient.get(`/auth/verify-reset-token/${token}`),
};

const sanitizeParams = (params = {}) => {
  const sanitized = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }
    if (typeof value === 'string' && value.trim() === '') {
      return;
    }
    sanitized[key] = value;
  });
  return sanitized;
};

// Users API
export const usersAPI = {
  getUsers: (params) => apiClient.get('/users', { params: sanitizeParams(params) }),
  getUser: (id) => apiClient.get(`/users/${id}`),
  updateUser: (id, data) => apiClient.put(`/users/${id}`, data),
  deactivateUser: (id) => apiClient.delete(`/users/${id}`),
  reactivateUser: (id) => apiClient.patch(`/users/${id}/reactivate`),
  deleteUser: (id) => apiClient.delete(`/users/${id}/delete`),
  undeleteUser: (id) => apiClient.patch(`/users/${id}/undelete`),
  getDeletedUsers: (params) => apiClient.get('/users/deleted', { params: sanitizeParams(params) }),
  resetPassword: (id, newPassword) => apiClient.post(`/users/${id}/reset-password`, { newPassword }),
};

// Clients API
export const clientsAPI = {
  getClients: (params) => apiClient.get('/clients', { params: sanitizeParams(params) }),
  getClient: (id) => apiClient.get(`/clients/${id}`),
  createClient: (data) => apiClient.post('/clients', data),
  techSubmitClient: (data) => apiClient.post('/clients/tech-submit', data),
  getPendingClients: () => apiClient.get('/clients/pending'),
  approveClient: (id) => apiClient.patch(`/clients/${id}/approve`),
  rejectClient: (id) => apiClient.delete(`/clients/${id}/reject`),
  updateClient: (id, data) => apiClient.put(`/clients/${id}`, data),
  updateClientGeneratorInfo: (id, data) => apiClient.put(`/clients/${id}/generator-info`, data),
  /** Technician-editable persistent note shown on every assignment card for this client. */
  updateTechNotes: (id, notes) => apiClient.put(`/clients/${id}/tech-notes`, { notes }),
  deactivateClient: (id) => apiClient.delete(`/clients/${id}`),
  reactivateClient: (id) => apiClient.patch(`/clients/${id}/reactivate`),
  getContractClientsNeedingService: (params) => apiClient.get('/clients/contract/needing-service', { params: sanitizeParams(params) }),
  getServiceHistoryOverview: (params) => apiClient.get('/clients/service-history-overview', { params: sanitizeParams(params) }),
  assignContractClient: (data) => apiClient.post('/clients/contract/assign', data),
  getWeeklyAssignments: () => apiClient.get('/clients/contract/weekly-assignments'),
  queueContractClient: (data) => apiClient.post('/clients/contract/queue', data),
  getQueuedServiceCalls: () => apiClient.get('/clients/contract/queue'),
  getQueuedServiceCallCount: () => apiClient.get('/clients/contract/queue/count'),
  updateQueuedServiceCall: (id, data) => apiClient.patch(`/clients/contract/queue/${id}`, data),
  removeQueuedServiceCall: (id) => apiClient.delete(`/clients/contract/queue/${id}`),
  unassignContractClient: (serviceCallId) => apiClient.delete(`/clients/contract/unassign/${serviceCallId}`),
  markClientDone: (clientId, data = {}) => apiClient.post(`/clients/contract/${clientId}/mark-done`, data),
  skipClientToNextCycle: (clientId, data = {}) => apiClient.post(`/clients/contract/${clientId}/skip-to-next-cycle`, data),
  pushBackClient: (clientId, data = {}) => apiClient.post(`/clients/contract/${clientId}/push-back`, data),
  assignPriorityServiceCall: (data) => apiClient.post('/clients/priority/assign', data),
  getPriorityAssignments: () => apiClient.get('/clients/priority/assignments'),
  unassignPriorityServiceCall: (serviceCallId) => apiClient.delete(`/clients/priority/unassign/${serviceCallId}`),
  getDuplicateCities: () => apiClient.get('/clients/cities/duplicates'),
  standardizeCity: (data) => apiClient.post('/clients/cities/standardize', data),
  getAddressFormattingIssues: () => apiClient.get('/clients/addresses/formatting-issues'),
  standardizeAddress: (clientId, data) => apiClient.put(`/clients/addresses/${clientId}/standardize`, data),
  getZipCodeIssues: () => apiClient.get('/clients/addresses/zipcode-issues'),
  autoResolveZipCodes: () => apiClient.post('/clients/addresses/auto-resolve-zipcodes'),
  resolveZipCode: (clientId, data) => apiClient.put(`/clients/addresses/${clientId}/resolve-zipcode`, data),
  getUnresolvedZipCodes: () => apiClient.get('/clients/addresses/unresolved-zipcodes'),
  geocodeAllClients: () => apiClient.post('/clients/addresses/geocode-all'),
  getGeocodingFailures: () => apiClient.get('/clients/addresses/geocoding-failures'),
  importClients: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post('/clients/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 300000, // 5 minutes timeout for large imports
    });
  },
  clearGeneratorInfo: () => apiClient.post('/clients/clear-generator-info'),
  checkMonthTransition: () => apiClient.get('/clients/contract/month-transition-check'),
  bulkCarryOverClients: (clientIds) => apiClient.post('/clients/contract/bulk-carryover', { clientIds }),
  getMonthPairDiff: () => apiClient.get('/clients/contract/month-pair-diff'),
};


// Service Calls API
export const serviceCallsAPI = {
  getServiceCalls: (params, axiosConfig = {}) =>
    apiClient.get('/service-calls', { params: sanitizeParams(params), ...axiosConfig }),
  getServiceCall: (id) => apiClient.get(`/service-calls/${id}`),
  createServiceCall: (data) => apiClient.post('/service-calls', data),
  createTechnicianServiceCall: (data) => apiClient.post('/service-calls/technician', data),
  updateServiceCall: (id, data) => apiClient.put(`/service-calls/${id}`, data),
  deleteServiceCall: (id) => apiClient.delete(`/service-calls/${id}`),
  bulkDeleteServiceCalls: (serviceCallIds) => apiClient.post('/service-calls/bulk-delete', { serviceCallIds }),
  deleteAllServiceCalls: () => apiClient.delete('/service-calls'),
  getDailyClients: (date) => apiClient.get(`/service-calls/daily/${date}`),
  getWeeklyAssignments: () => apiClient.get('/service-calls/weekly-assignments'),
  getPriorityAssignments: () => apiClient.get('/service-calls/priority-assignments'),
  finalizeServiceCall: (id) => apiClient.patch(`/service-calls/${id}/finalize`),
  toggleCorrectionFlag: (id, data) => apiClient.patch(`/service-calls/${id}/correction-flag`, data),
  getMonthlyOilFilters: (month) => apiClient.get('/service-calls/monthly-oil-filters', { params: { month } }),
  getMonthlyFuelFilters: (month) => apiClient.get('/service-calls/monthly-fuel-filters', { params: { month } }),
};

// Service Call Visits API
export const serviceCallVisitsAPI = {
  getVisits: (serviceCallId) => apiClient.get(`/service-calls/${serviceCallId}/visits`),
  createVisit: (serviceCallId, data) => apiClient.post(`/service-calls/${serviceCallId}/visits`, data),
  getVisit: (serviceCallId, visitId) => apiClient.get(`/service-calls/${serviceCallId}/visits/${visitId}`),
  updateVisit: (serviceCallId, visitId, data) => apiClient.put(`/service-calls/${serviceCallId}/visits/${visitId}`, data),
  deleteVisit: (serviceCallId, visitId) => apiClient.delete(`/service-calls/${serviceCallId}/visits/${visitId}`),
};

// Service Call Types API
export const serviceCallTypesAPI = {
  getServiceCallTypes: (params) => apiClient.get('/service-call-types', { params: sanitizeParams(params) }),
  getServiceCallType: (id) => apiClient.get(`/service-call-types/${id}`),
  createServiceCallType: (data) => apiClient.post('/service-call-types', data),
  updateServiceCallType: (id, data) => apiClient.put(`/service-call-types/${id}`, data),
  deleteServiceCallType: (id) => apiClient.delete(`/service-call-types/${id}`),
};

// Client Flags API
export const clientFlagsAPI = {
  getClientFlags: (params) => apiClient.get('/client-flags', { params: sanitizeParams(params) }),
  getClientFlag: (id) => apiClient.get(`/client-flags/${id}`),
  createClientFlag: (data) => apiClient.post('/client-flags', data),
  updateClientFlag: (id, data) => apiClient.put(`/client-flags/${id}`, data),
  deleteClientFlag: (id) => apiClient.delete(`/client-flags/${id}`),
};

// Inventory API
export const inventoryAPI = {
  getInventory: (params) => apiClient.get('/inventory', { params: sanitizeParams(params) }),
  getInventoryItem: (id) => apiClient.get(`/inventory/${id}`),
  createInventoryItem: (data) => apiClient.post('/inventory', data),
  updateInventoryItem: (id, data) => apiClient.put(`/inventory/${id}`, data),
  deleteInventoryItem: (id) => apiClient.delete(`/inventory/${id}`),
  importInventory: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post('/inventory/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 300000, // 5 minutes timeout for large imports
    });
  },
};

// Parts Needed API
export const partsNeededAPI = {
  getPartsNeeded: (params) => apiClient.get('/parts-needed', { params: sanitizeParams(params) }),
  getPartNeeded: (id) => apiClient.get(`/parts-needed/${id}`),
  createPartNeeded: (data) => apiClient.post('/parts-needed', data),
  updatePartNeeded: (id, data) => apiClient.put(`/parts-needed/${id}`, data),
  deletePartNeeded: (id) => apiClient.delete(`/parts-needed/${id}`),
  getPartsNeededSummary: () => apiClient.get('/parts-needed/summary')
};

// Reports API
export const reportsAPI = {
  getTechMetrics: (params) => apiClient.get('/reports/tech-metrics', { params: sanitizeParams(params) }),
  getTechCorrections: (params) => apiClient.get('/reports/tech-corrections', { params: sanitizeParams(params) }),
};

// CSpire API
export const cspireAPI = {
  getSites: (params) => apiClient.get('/cspire/sites', { params: sanitizeParams(params) }),
  createSite: (data) => apiClient.post('/cspire/sites', data),
  updateSite: (id, data) => apiClient.put(`/cspire/sites/${id}`, data),
  /** Technician-only: generator / filter fields when assigned to this C Spire site (or needs-tech-info). */
  updateSiteByTechnician: (id, data) => apiClient.patch(`/cspire/sites/${id}/technician-update`, data),
  /** Technician-editable persistent note shown on every assignment card for this CSpire site. */
  updateSiteTechNotes: (id, notes) => apiClient.put(`/cspire/sites/${id}/tech-notes`, { notes }),
  getWorkOrders: (params) => apiClient.get('/cspire/work-orders', { params: sanitizeParams(params) }),
  createWorkOrder: (data) => apiClient.post('/cspire/work-orders', data),
  updateWorkOrder: (id, data) => apiClient.put(`/cspire/work-orders/${id}`, data),
  deleteWorkOrder: (id, { force = false } = {}) => apiClient.delete(`/cspire/work-orders/${id}`, { params: force ? { force: 'true' } : {} }),
  deleteAllWorkOrders: () => apiClient.delete('/cspire/work-orders'),
  findDuplicateWorkOrders: () => apiClient.get('/cspire/work-orders/duplicates'),
  updateWorkOrderSchedule: (id, data) => apiClient.patch(`/cspire/work-orders/${id}/schedule`, data),
  assignWorkOrder: (id, data) => apiClient.post(`/cspire/work-orders/${id}/assign`, data),
  getPriceSchedules: (params) => apiClient.get('/cspire/price-schedules', { params: sanitizeParams(params) }),
  createPriceSchedule: (data) => apiClient.post('/cspire/price-schedules', data),
  updatePriceSchedule: (id, data) => apiClient.put(`/cspire/price-schedules/${id}`, data),
  deletePriceSchedule: (id) => apiClient.delete(`/cspire/price-schedules/${id}`)
};

// Franklin API
export const franklinAPI = {
  getSites: (params) => apiClient.get('/franklin/sites', { params: sanitizeParams(params) }),
  getSite: (id) => apiClient.get(`/franklin/sites/${id}`),
  createSite: (data) => apiClient.post('/franklin/sites', data),
  updateSite: (id, data) => apiClient.put(`/franklin/sites/${id}`, data),
  /** Technician-only: equipment fields when assigned to this Franklin site (or needs-tech-info). */
  updateSiteByTechnician: (id, data) => apiClient.patch(`/franklin/sites/${id}/technician-update`, data),
  /** Technician-editable persistent note shown on every assignment card for this Franklin site. */
  updateSiteTechNotes: (id, notes) => apiClient.put(`/franklin/sites/${id}/tech-notes`, { notes }),
  assignSite: (id, data) => apiClient.post(`/franklin/sites/${id}/assign`, data),
  getSitesNeedingService: () => apiClient.get('/franklin/sites/needing-service'),
  importSites: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post('/franklin/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000
    });
  }
};

export default apiClient;
